import { useState } from "react";
import { Tabs, VStack, HStack, FileUpload, Button, Input, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";

import CameraSelection from "../CameraSelection/CameraSelection";
import CreateCalibration from "../CreateCalibration/CreateCalibration";
import ViewCalibration from "../ViewCalibration/ViewCalibration";
import TestCalibration from "../TestCalibration/TestCalibration";

function base64ToBlob(base64Data, contentType = "image/png") {
  // 1. Safely extract just the base64 character string
  const base64Body = base64Data.includes(",") ? base64Data.split(",")[1] : base64Data;

  // 2. Decode the string into binary text
  const byteCharacters = atob(base64Body);

  // 3. Allocate an actual byte array buffer matching the exact size
  const byteNumbers = new Uint8Array(byteCharacters.length);

  // 4. Fill the buffer directly
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  // 5. Return the native Blob
  return new Blob([byteNumbers], { type: contentType });
}

const fetchVideoImage = async (video_url, timestamp) => {
  const params = new URLSearchParams({ video_url: video_url, timestamp: timestamp });
  const response = await fetch("/api/video_frame?" + params, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return response.json();
};

const CameraSetup = () => {
  const [setupId, setSetupId] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [calibrationImage, setCalibrationImage] = useState(null);

  const onImageFileChange = (details) => {
    setCalibrationImage(details.acceptedFiles[0]);
  };

  const loadImageFromURL = async () => {
    const { data: downloadedData } = await refetch();
    if (downloadedData) {
      setCalibrationImage(base64ToBlob(downloadedData["frame"]));
    }
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["/api/video_frame", videoUrl],
    queryFn: () => fetchVideoImage(videoUrl, 60),
    initialData: null,
    enabled: false,
    timeToStale: Infinity,
  });

  return (
    <VStack>
      <HStack width="100%" gap="2">
        <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">
          Calibration image
        </Text>
        <FileUpload.Root onFileChange={(details) => onImageFileChange(details)} width="auto">
          <FileUpload.HiddenInput />
          <FileUpload.Trigger asChild>
            <Button size="sm" variant="outline">
              Load File
            </Button>
          </FileUpload.Trigger>
        </FileUpload.Root>
        <Text fontSize="sm">or</Text>
        <Input
          size="sm"
          maxWidth="400px"
          placeholder="Video URL"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
        />
        <Button size="sm" variant="outline" onClick={loadImageFromURL} disabled={isFetching || videoUrl === ""}>
          {isFetching ? "Loading..." : "Load From Video"}
        </Button>
      </HStack>

      <Tabs.Root defaultValue="camera_selection" width="100%">
        <Tabs.List>
          <Tabs.Trigger value="camera_selection">Camera Selection</Tabs.Trigger>
          <Tabs.Trigger value="create_calibration">Create Calibration</Tabs.Trigger>
          <Tabs.Trigger value="view_calibration">View Calibration</Tabs.Trigger>
          <Tabs.Trigger value="test_calibration">Test Calibration</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="camera_selection">
          <CameraSelection
            selectedSetupId={setupId}
            setSelectedSetupId={setSetupId}
            calibrationImage={calibrationImage}
          ></CameraSelection>
        </Tabs.Content>
        <Tabs.Content value="create_calibration">
          <CreateCalibration
            selectedSetupId={setupId}
            setSelectedSetupId={setSetupId}
            calibrationImage={calibrationImage}
          ></CreateCalibration>
        </Tabs.Content>
        <Tabs.Content value="view_calibration">
          <ViewCalibration selectedSetupId={setupId} setSelectedSetupId={setSetupId}></ViewCalibration>
        </Tabs.Content>

        <Tabs.Content value="test_calibration">
          <TestCalibration
            selectedSetupId={setupId}
            setSelectedSetupId={setSetupId}
            calibrationImage={calibrationImage}
          ></TestCalibration>
        </Tabs.Content>
      </Tabs.Root>
    </VStack>
  );
};

export default CameraSetup;
